import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { usePresence } from "@/lib/PresenceContext";
import PresenceDot from "@/components/PresenceDot";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";
import Avatar from "@/components/Avatar";
import { api } from "@/lib/api";

export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { ready, supabaseUserId } = useSupabaseSession();
  const { isOnline } = usePresence();
  const { lang } = useT();
  const isDE = (lang || "de").startsWith("de");

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(conversationId || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [profilesByUser, setProfilesByUser] = useState({});
  const [typingUsernames, setTypingUsernames] = useState([]);
  const endRef = useRef(null);
  const typingChRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Load conversation list (with last message preview + counterpart profile)
  // Uses backend (service-role) — bypasses RLS + JWT-bridge timing issues so
  // the sidebar always populates, even on iOS Safari where the Supabase
  // session can lag behind the FastAPI session cookie.
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/dm/conversations");
      const items = (data?.items || []).map((c) => ({
        conversation_id: c.conversation_id,
        last_read_at: c.last_read_at,
        conversations: { id: c.conversation_id, last_message_at: c.last_message_at },
        counterpart: c.counterpart || null,
      }));
      setConversations(items);
      const map = {};
      items.forEach((c) => { if (c.counterpart?.user_id) map[c.counterpart.user_id] = c.counterpart; });
      setProfilesByUser((prev) => ({ ...prev, ...map }));
    } catch (_e) { /* noop */ }
  }, []);

  // Load messages for active conv via the backend endpoint.
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/dm/${convId}/messages`);
      setMessages(data?.items || []);
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => {
    if (conversationId) setActiveConv(conversationId);
  }, [conversationId]);
  useEffect(() => {
    if (activeConv) loadMessages(activeConv);
  }, [activeConv, loadMessages]);

  // Realtime new messages — guard against duplicates from optimistic insert.
  // When the Supabase JWT bridge isn't ready (e.g. fresh iOS Safari load),
  // fall back to a 4s poll so the UI still updates.
  useEffect(() => {
    if (!activeConv) return;
    if (ready && supabase) {
      const ch = supabase.channel(`msgs:${activeConv}`)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${activeConv}` },
          (payload) => {
            setMessages((m) => {
              if (m.some((x) => x.id === payload.new.id)) return m;
              return [...m, payload.new];
            });
          },
        )
        .subscribe();
      return () => { supabase.removeChannel(ch); };
    }
    // Fallback: polling
    const t = setInterval(() => loadMessages(activeConv), 4000);
    return () => clearInterval(t);
  }, [ready, activeConv, loadMessages]);

  // Typing indicator via broadcast channel (no DB writes)
  useEffect(() => {
    if (!ready || !supabase || !activeConv || !supabaseUserId) return;
    const myName = profilesByUser[supabaseUserId]?.username
      || (conversations.find((c) => c.conversation_id === activeConv)?.counterpart?.username ? "Me" : "Me");
    const ch = supabase.channel(`typing:${activeConv}`, { config: { broadcast: { self: false } } })
      .on("broadcast", { event: "typing" }, ({ payload }) => {
        const name = payload?.username;
        if (!name) return;
        setTypingUsernames((prev) => prev.includes(name) ? prev : [...prev, name]);
        // auto-clear after 3.5s
        setTimeout(() => setTypingUsernames((prev) => prev.filter((n) => n !== name)), 3500);
      })
      .subscribe();
    typingChRef.current = { ch, myName };
    return () => {
      supabase.removeChannel(ch);
      typingChRef.current = null;
      setTypingUsernames([]);
    };
  }, [ready, activeConv, supabaseUserId, profilesByUser, conversations]);

  const sendTyping = () => {
    const handle = typingChRef.current;
    if (!handle) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1500) return;  // throttle
    lastTypingSentRef.current = now;
    handle.ch.send({ type: "broadcast", event: "typing", payload: { username: handle.myName } });
  };

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, typingUsernames.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t || !activeConv || !supabaseUserId) return;
    setText("");
    // Optimistic bubble so iOS Safari feels instant. Reconciled below with
    // either the backend's authoritative row OR the realtime echo (dedup by id).
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      conversation_id: activeConv,
      sender_id: supabaseUserId,
      content: t,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((m) => [...m, optimistic]);

    try {
      const { data } = await api.post("/dm/send", {
        conversation_id: activeConv,
        content: t,
      });
      const row = data?.message || null;
      if (row?.id) {
        setMessages((m) => {
          const without = m.filter((x) => x.id !== tempId && x.id !== row.id);
          return [...without, row];
        });
      } else {
        // No row returned but no error either — reload as a safety net.
        setMessages((m) => m.filter((x) => x.id !== tempId));
        loadMessages(activeConv);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[dm] /dm/send failed", err?.response?.status, err?.response?.data);
      setText(t);
      setMessages((m) => m.filter((x) => x.id !== tempId));
      const code = err?.response?.data?.detail?.error || err?.response?.data?.detail;
      const friendly = code === "not_a_participant"
        ? (isDE ? "Du bist kein Teil dieser Konversation" : "You're not in this conversation")
        : code === "empty_content"
        ? (isDE ? "Nachricht ist leer" : "Message is empty")
        : code === "content_too_long"
        ? (isDE ? "Nachricht ist zu lang" : "Message too long")
        : (isDE ? "Nachricht konnte nicht gesendet werden" : "Couldn't send message");
      try {
        const toastMod = await import("sonner");
        toastMod.toast.error(friendly);
      } catch (_e) { /* noop */ }
    }
  };

  // No "Chat wird verbunden…" block — the backend endpoints don't depend on
  // the Supabase JWT bridge, so we render immediately and let realtime
  // upgrade in the background when `ready` flips to true.

  return (
    <AppShell>
      <PageContainer className="!max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 sm:gap-5 h-[calc(100vh-160px)] min-h-[500px]">
          {/* Sidebar */}
          <aside className={`rounded-2xl border border-white/5 bg-zinc-900/50 overflow-y-auto ${activeConv ? "hidden md:block" : ""}`} data-testid="chat-sidebar">
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
              <MessageSquare size={14} className="text-neon" />
              <h2 className="font-display font-bold text-sm">{isDE ? "Direktnachrichten" : "Direct Messages"}</h2>
            </div>
            {conversations.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-zinc-500">
                {isDE ? "Noch keine Chats. Eröffne einen aus einem Profil." : "No chats yet. Open one from a profile."}
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {conversations.map((c) => {
                  const isActive = activeConv === c.conversation_id;
                  const cp = c.counterpart;
                  return (
                    <li key={c.conversation_id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/app/chat/${c.conversation_id}`)}
                        data-testid={`chat-conv-${c.conversation_id}`}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isActive ? "bg-neon/10" : "hover:bg-white/5"}`}
                      >
                        <div className="relative">
                          <Avatar src={cp?.avatar_url} name={cp?.username} size={36} />
                          {(isOnline(cp?.user_id) || cp?.is_online) && (
                            <PresenceDot userId={cp?.user_id} size={11} className="absolute -bottom-0.5 -right-0.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{cp?.username || "User"}</p>
                          <p className="text-[11px] text-zinc-500">{(isOnline(cp?.user_id) || cp?.is_online) ? (isDE ? "Online" : "Online") : (isDE ? "Offline" : "Offline")}</p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          {/* Conversation */}
          <section className={`rounded-2xl border border-white/5 bg-zinc-900/50 flex flex-col overflow-hidden ${!activeConv ? "hidden md:flex" : ""}`} data-testid="chat-window">
            {!activeConv ? (
              <div className="flex-1 grid place-items-center text-zinc-500 text-sm p-6 text-center">
                {isDE ? "Wähle einen Chat aus" : "Pick a chat"}
              </div>
            ) : (
              <>
                <header className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                  <button type="button" onClick={() => navigate("/app/chat")} className="md:hidden text-zinc-400 hover:text-neon">
                    <ArrowLeft size={16} />
                  </button>
                  <h2 className="font-display font-bold text-sm">
                    {(conversations.find((c) => c.conversation_id === activeConv)?.counterpart?.username) || "Chat"}
                  </h2>
                </header>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {loading ? (
                    <div className="text-center text-xs text-zinc-500 py-8">{isDE ? "Lade…" : "Loading…"}</div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-xs text-zinc-500 py-8">{isDE ? "Sag Hi 👋" : "Say hi 👋"}</div>
                  ) : (
                    messages.map((m) => {
                      const mine = m.sender_id === supabaseUserId;
                      return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${mine ? "bg-neon text-black font-medium rounded-tr-sm" : "bg-zinc-950/80 text-zinc-100 border border-white/5 rounded-tl-sm"}`} data-testid={`chat-msg-${m.id}`}>
                            {m.content}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={endRef} />
                </div>
                {typingUsernames.length > 0 && (
                  <div className="px-4 py-1 text-[11px] text-zinc-400 italic flex items-center gap-1" data-testid="chat-typing">
                    <span className="inline-flex gap-0.5">
                      <span className="w-1 h-1 rounded-full bg-neon animate-pulse" />
                      <span className="w-1 h-1 rounded-full bg-neon animate-pulse" style={{ animationDelay: "0.15s" }} />
                      <span className="w-1 h-1 rounded-full bg-neon animate-pulse" style={{ animationDelay: "0.3s" }} />
                    </span>
                    {typingUsernames.join(", ")} {isDE ? "tippt…" : "is typing…"}
                  </div>
                )}
                <form onSubmit={send} className="p-3 border-t border-white/5 flex gap-2" data-testid="chat-composer">
                  <Input
                    value={text}
                    onChange={(e) => { setText(e.target.value); sendTyping(); }}
                    placeholder={isDE ? "Nachricht schreiben…" : "Type a message…"}
                    className="flex-1 bg-zinc-950 border-white/10"
                    data-testid="chat-input"
                  />
                  <Button type="submit" disabled={!text.trim()} className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold" data-testid="chat-send">
                    <Send size={14} />
                  </Button>
                </form>
              </>
            )}
          </section>
        </div>
      </PageContainer>
    </AppShell>
  );
}
